import { z } from 'zod'
import type { AuthorizedAdvertiserId } from './authorized-advertiser-id.ts'
import type { AuthorizedAdvertiserUrl } from './authorized-advertiser-url.ts'

type AdvertiserDetails = {
  advertiserId: AuthorizedAdvertiserId
  companyName: string
  logo: string | null
  url: AuthorizedAdvertiserUrl
  lastLoginTimeForUser: string | null
}

type AdvertiserTenant = {
  kind: 'advertiser'
  current: AdvertiserDetails
  advertiserIds: number[]
  loginAs: LoginAsMode
  isBeta: boolean
  isAlpha: boolean
}

type PublisherTenant = {
  kind: 'publisher'
  publisherId: number
}

type NoneTenant = {
  kind: 'none'
  advertiserIds: number[]
}

export type Tenant = AdvertiserTenant | PublisherTenant | NoneTenant

type TenantKind = Tenant['kind']

export type TenantFromKind<K extends TenantKind> = Extract<Tenant, { kind: K }>
export type Kind<Value extends TenantKind = TenantKind> = [Value] extends [TenantKind]
  ? { tenant: TenantFromKind<Extract<Value, TenantKind>> }
  : never
export type LoginAs<Value extends LoginAsMode> = Kind<'advertiser'> & {
  tenant: { loginAs: Value }
}

export function KindSchema<const Value extends TenantKind | readonly TenantKind[]>(kind: Value) {
  type MatchedKind = Value extends readonly TenantKind[] ? Value[number] : Value
  const kinds: readonly TenantKind[] = Array.isArray(kind) ? kind : [kind]
  const schema = z.object({ tenant: z.object({ kind: z.string() }) })

  return z.custom<Kind<Extract<MatchedKind, TenantKind>>>(value => {
    const result = schema.safeParse(value)
    return result.success && kinds.some(kind => kind === result.data.tenant.kind)
  })
}

export function LoginAsSchema<const Value extends LoginAsMode>(loginAs: Value) {
  const schema = z.object({
    tenant: z.object({
      kind: z.literal('advertiser'),
      loginAs: z.literal(loginAs),
    }),
  })
  return z.custom<LoginAs<Value>>(value => schema.safeParse(value).success)
}

type LoginAsMode = 'admin' | 'demo' | 'advertiser'
